import "./ConstructorTable.style.css";

import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { ObjectSettings } from "../ObjectSettings/ObjectSettings";
import { PropertySettings } from "../PropertySettings/PropertySettings";
import { ObjectState } from "../../../utils/states/ConstructorPageStates/ObjectState";
import { PropertyState } from "../../../utils/states/ConstructorPageStates/PropertyState";
import { PropertyType } from "../../../utils/enums/PropertyType";
import { UniqueId } from "../../../utils/UniqueId";
import { PropertyColumn } from "./PropertyColumn/PropertyColumn";
import { getDefaultValueForType } from "../../../utils/functions/getDefaultValueForType";
import { Status } from "../../../utils/enums/Status";
import { ObjectValue } from "../../../utils/states/ConstructorPageStates/ObjectValue";
import {
  DEFAULT_OBJECT_IMAGE,
  MAX_FILE_SIZE_MB,
} from "../../../utils/constants/constants";

import plusImage from "./../../../resources/images/plus.png";

interface GameTableProps {
  properties: PropertyState[];
  setProperties: Dispatch<SetStateAction<PropertyState[]>>;
  objects: ObjectState[];
  setObjects: Dispatch<SetStateAction<ObjectState[]>>;
}

export const ConstructorTable = ({
  setProperties,
  properties,
  objects,
  setObjects,
}: GameTableProps) => {
  const [editedObject, setEditedObject] = useState<ObjectState | null>(null);
  const objectRef = useRef<HTMLDivElement | null>(null);

  const [editedProperty, setEditedProperty] = useState<PropertyState | null>(
    null
  );
  const propertyRef = useRef<HTMLDivElement | null>(null);

  const [draggedItem, setDraggedItem] = useState<
    keyof ObjectState["properties"] | null
  >(null);
  const [draggedOverItem, setDraggedOverItem] = useState<
    keyof ObjectState["properties"] | null
  >(null);

  const titleRefs = useRef<Array<HTMLDivElement | null>>([]);

  const addPropertyRef = useRef<HTMLButtonElement | null>(null);
  const addObjectRef = useRef<HTMLButtonElement | null>(null);

  const objectSettingsRef = useRef<HTMLDivElement | null>(null);
  const propertySettingsRef = useRef<HTMLDivElement | null>(null);

  const alignColumns = useCallback(() => {
    titleRefs.current.forEach((el) => {
      if (el) {
        el.style.height = "auto";
      }
    });

    const heights = Array.from(titleRefs.current.values()).map(
      (el) => el?.offsetHeight || 0
    );

    const maxHeight = heights.length > 0 ? Math.max(...heights) : 0;

    titleRefs.current.forEach((el) => {
      if (el) {
        el.style.height = `${maxHeight}px`;
      }
    });
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    alignColumns();
  }, [properties, objects, alignColumns]);

  const handleClickOutside = (e: MouseEvent) => {
    const isEditedObject =
      objectRef.current && objectRef.current.contains(e.target as Node);
    const isAddedObject =
      addObjectRef.current && addObjectRef.current.contains(e.target as Node);
    const isObjectSettingsObject =
      objectSettingsRef.current &&
      objectSettingsRef.current.contains(e.target as Node);

    if (!isEditedObject && !isAddedObject && !isObjectSettingsObject) {
      setEditedObject(null);
    }

    const isEditedProperty =
      propertyRef.current && propertyRef.current.contains(e.target as Node);
    const isAddedProperty =
      addPropertyRef.current &&
      addPropertyRef.current.contains(e.target as Node);
    const isPropertySettingsObject =
      propertySettingsRef.current &&
      propertySettingsRef.current.contains(e.target as Node);

    if (!isEditedProperty && !isAddedProperty && !isPropertySettingsObject) {
      setEditedProperty(null);
    }
  };

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    id: keyof ObjectState["properties"]
  ) => {
    e.dataTransfer.setData("text/plain", id.toString());

    setDraggedItem(id);

    e.currentTarget.classList.add("is-dragging");
  };

  const handleDragEnter = (
    e: React.DragEvent<HTMLDivElement>,
    property: keyof ObjectState["properties"]
  ) => {
    e.preventDefault();

    if (draggedItem && draggedItem !== property) {
      setDraggedOverItem(property);
    }
  };

  const handleDragLeave = () => {
    if (draggedOverItem) {
      setDraggedOverItem(null);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    targetFieldId: number
  ) => {
    e.preventDefault();
    const draggedFieldId = parseInt(e.dataTransfer.getData("text/plain"), 10);

    if (!draggedFieldId || draggedFieldId === targetFieldId) {
      setDraggedItem(null);
      setDraggedOverItem(null);

      const draggedElements = document.querySelectorAll(".is-dragging");
      draggedElements.forEach((el) => el.classList.remove("is-dragging"));
      return;
    }

    setProperties((prevProperties) => {
      const newProperties = [...prevProperties];

      const draggedIndex = newProperties.findIndex(
        (prop) => prop.id === draggedFieldId
      );
      const targetIndex = newProperties.findIndex(
        (prop) => prop.id === targetFieldId
      );

      if (draggedIndex === -1 || targetIndex === -1) {
        return prevProperties;
      }

      const [removed] = newProperties.splice(draggedIndex, 1);
      newProperties.splice(targetIndex, 0, removed);

      return newProperties;
    });

    setDraggedItem(null);
    setDraggedOverItem(null);

    const draggedElements = document.querySelectorAll(".is-dragging");
    draggedElements.forEach((el) => el.classList.remove("is-dragging"));
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    setDraggedItem(null);
    setDraggedOverItem(null);
    e.currentTarget.classList.remove("is-dragging");

    const dragOverElements = document.querySelectorAll(".is-drag-over");
    dragOverElements.forEach((el) => el.classList.remove("is-drag-over"));
  };

  const handleObjectClicked = (objectState: ObjectState) => {
    setEditedObject(objectState);

    setEditedProperty(null);
  };

  const handlePropertyClicked = (propertyState: PropertyState) => {
    setEditedProperty(propertyState);

    setEditedObject(null);
  };

  const handleAddPropertyClicked = () => {
    const newProperty: PropertyState = {
      id: UniqueId.generateUniqueId(),
      name: "Новое свойство",
      type: PropertyType.Integer,
      status: Status.Create,
      apiId: null,
    };

    setProperties((prevState) => [...prevState, newProperty]);

    setObjects((prevState) =>
      prevState.map((object) => ({
        ...object,
        status: object.apiId ? Status.Update : object.status,
        properties: {
          ...object.properties,
          [newProperty.id]: {
            type: newProperty.type,
            value: getDefaultValueForType(newProperty.type),
          },
        },
      }))
    );

    setEditedProperty(newProperty);
    setEditedObject(null);
  };

  const handleDeletePropertyClicked = () => {
    if (!editedProperty) {
      return;
    }

    if (!editedProperty.apiId) {
      setProperties((prevState) =>
        prevState.filter((property) => property.id !== editedProperty.id)
      );
      UniqueId.deleteUniqueId(editedProperty.id);
    } else {
      setProperties((prevState) =>
        prevState.map((property) =>
          property.id !== editedProperty.id
            ? property
            : {
                ...property,
                status: Status.Delete,
              }
        )
      );
    }

    setObjects((prevState) =>
      prevState.map((object) => {
        const newProperties = { ...object.properties };
        delete newProperties[editedProperty.id];
        return {
          ...object,
          properties: newProperties,
          status: object.apiId ? Status.Update : object.status,
        };
      })
    );

    setEditedProperty(null);
  };

  const handleChangePropertyName = (newProperty: PropertyState) => {
    if (editedProperty) {
      setProperties((prevState) =>
        prevState.map((property) =>
          property.id === editedProperty.id ? newProperty : property
        )
      );

      setEditedProperty(newProperty);
    }
  };

  const handleChangePropertyType = (newProperty: PropertyState) => {
    if (editedProperty) {
      setProperties((prevState) =>
        prevState.map((property) =>
          property.id === editedProperty.id ? newProperty : property
        )
      );

      setObjects((prevState) =>
        prevState.map((object) => {
          return {
            ...object,
            status: object.apiId ? Status.Update : object.status,
            properties: {
              ...object.properties,
              [newProperty.id]: {
                type: newProperty.type,
                value: getDefaultValueForType(newProperty.type),
              },
            },
          };
        })
      );

      setEditedProperty(newProperty);
    }
  };

  const handleAddObjectClicked = () => {
    const newObject: ObjectState = {
      id: UniqueId.generateUniqueId(),
      apiId: null,
      image: DEFAULT_OBJECT_IMAGE,
      name: "Новый объект",
      status: Status.Create,
      properties: properties.reduce((acc, property) => {
        acc[property.id] = {
          type: property.type,
          value: getDefaultValueForType(property.type),
        };
        return acc;
      }, {} as { [propertyId: number]: ObjectValue }),
    };

    setObjects((prevState) => [...prevState, newObject]);

    setEditedObject(newObject);
    setEditedProperty(null);
  };

  const handleChangeObjectValue = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    const [objectId, propertyId] = name.split("_").map((id) => parseInt(id));

    setObjects((prevObjects) => {
      return prevObjects.map((object) => {
        if (object.id === objectId) {
          const objectValue = object.properties[propertyId];

          return {
            ...object,
            status: object.apiId ? Status.Update : object.status,
            properties: {
              ...object.properties,
              [propertyId]: { type: objectValue.type, value: value },
            },
          };
        }

        return object;
      });
    });

    setEditedObject((prevState) => {
      if (!prevState) {
        return null;
      }

      const objectValue = prevState.properties[propertyId];

      return {
        ...prevState,
        status: prevState.apiId ? Status.Update : prevState.status,
        properties: {
          ...prevState.properties,
          [propertyId]: { type: objectValue.type, value: value },
        },
      };
    });
  };

  const handleChangeObject = (newObject: ObjectState) => {
    if (editedObject) {
      setObjects((prevObjects) =>
        prevObjects.map((obj) =>
          obj.id === editedObject?.id ? newObject : obj
        )
      );

      setEditedObject(newObject);
    }
  };

  const handleObjectValueImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const { name } = e.target;

    const [objectId, propertyId] = name.split("_").map((id) => parseInt(id));

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      console.error("Слишком большой файл");
      return;
    }

    const reader = new FileReader();

    reader.onload = (event: ProgressEvent<FileReader>) => {
      const result = event.target?.result as string;
      setObjects((prevObjects) => {
        return prevObjects.map((obj) => {
          if (obj.id === objectId) {
            const objectValue = obj.properties[propertyId];

            return {
              ...obj,
              properties: {
                ...obj.properties,
                [propertyId]: { type: objectValue.type, value: result },
              },
              status: obj.apiId ? Status.Update : obj.status,
            };
          }

          return obj;
        });
      });

      setEditedObject((prevState) => {
        if (!prevState) {
          return null;
        }

        const objectValue = prevState.properties[propertyId];

        return {
          ...prevState,
          status: prevState.apiId ? Status.Update : prevState.status,
          properties: {
            ...prevState.properties,
            [propertyId]: { type: objectValue.type, value: result },
          },
        };
      });
    };

    reader.onerror = () => {
      console.error("Не получилось прочитать файл");
    };

    reader.readAsDataURL(file);
  };

  return (
    <div className="constructor_table_container">
      {!properties.length && !objects.length ? (
        <div className="constructor_table_start_block">
          <div className="constructor_table_start_block_title text_1">
            Добро пожаловать в конструктор игр YOURDLE!
            <br />
            Начните создавать свою игру со столбца или объекта
          </div>
          <div className="constructor_table_start_buttons">
            <button
              ref={addPropertyRef}
              onClick={handleAddPropertyClicked}
              className="constructor_table__start_property_add_button header_4"
            >
              Создайте первый столбец
            </button>
            <button
              ref={addObjectRef}
              onClick={handleAddObjectClicked}
              className="constructor_page__start_object_add_button header_4"
            >
              Создайте первый объект
            </button>
          </div>
        </div>
      ) : (
        <div className="constructor_table__object_container">
          <div className="constructor_table__columns">
            {objects.length !== 0 && (
              <div className="constructor_table__object_names_column">
                {properties.length !== 0 && (
                  <div
                    className="constructor_table__property_title hidden"
                    ref={(el) => {
                      titleRefs.current[0] = el;
                    }}
                  />
                )}
                {properties.length !== 0 && (
                  <div className="constructor_table__property_separator hidden" />
                )}
                {objects.map((object) => (
                  <div
                    className={
                      editedObject?.id === object.id
                        ? "constructor_table__object_name edited game_header"
                        : "constructor_table__object_name game_header"
                    }
                    ref={(el) => {
                      if (editedObject?.id === object.id) {
                        objectRef.current = el;
                      }
                    }}
                    onClick={() => handleObjectClicked(object)}
                    key={object.id}
                  >
                    <div
                      className={
                        editedObject?.id === object.id
                          ? "constructor_table__object_name_value editedImage"
                          : "constructor_table__object_name_value"
                      }
                    >
                      {object.name}
                    </div>
                    <img
                      className="constructor_table__object_name_image"
                      src={object.image}
                      alt="object_image"
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="constructor_table__property_columns">
              {properties.map((property, index) => (
                <PropertyColumn
                  key={property.id}
                  property={property}
                  objects={objects}
                  editedProperty={editedProperty}
                  editedObject={editedObject}
                  handlePropertyClicked={() => handlePropertyClicked(property)}
                  handleDragStart={(e) => handleDragStart(e, property.id)}
                  handleDragEnter={(e) => handleDragEnter(e, property.id)}
                  handleDragLeave={handleDragLeave}
                  handleDragOver={handleDragOver}
                  handleDrop={(e) => handleDrop(e, property.id)}
                  handleDragEnd={handleDragEnd}
                  handleChangeObjectValue={handleChangeObjectValue}
                  titleRefs={titleRefs}
                  index={index}
                  propertyRef={propertyRef}
                  handleObjectValueImageUpload={handleObjectValueImageUpload}
                />
              ))}
              {properties.length < 8 && (
                <div className="constructor_table__property_add_button_column">
                  <button
                    ref={addPropertyRef}
                    onClick={handleAddPropertyClicked}
                    className="constructor_table__property_add_button"
                  >
                    <img src={plusImage} alt="add button" />
                  </button>
                </div>
              )}
            </div>
          </div>
          <button
            ref={addObjectRef}
            onClick={handleAddObjectClicked}
            className="constructor_page__object_add_button header_4"
          >
            Добавить объект
          </button>
        </div>
      )}

      {editedObject && (
        <ObjectSettings
          handleChangeObject={handleChangeObject}
          setObjects={setObjects}
          setEditedObject={setEditedObject}
          editedObject={editedObject}
          properties={properties}
          objectSettingsRef={objectSettingsRef}
          handleValueChange={handleChangeObjectValue}
        />
      )}
      {editedProperty && (
        <PropertySettings
          editedProperty={editedProperty}
          handleChangePropertyName={handleChangePropertyName}
          handleChangePropertyType={handleChangePropertyType}
          handleDeletePropertyClicked={handleDeletePropertyClicked}
          propertySettingsRef={propertySettingsRef}
        />
      )}
    </div>
  );
};
